import React, { useEffect, useState, useRef } from "react";
import { db } from "../../firebase";
import {
  collection, getDocs, doc, setDoc, getDoc,
  updateDoc, deleteDoc, serverTimestamp, writeBatch, increment,
  query, orderBy, limit, startAfter, getCountFromServer,
} from "firebase/firestore";
import { isPointInPolygon } from "geolib";
import Modal from "../../components/Modal";
import UISwitch from "../../components/UISwitch";
import { useLanguage } from "../../context/LanguageContext";
import {
  ClipboardList, MapPin,
  ChevronLeft, ChevronRight,
} from "lucide-react";function ClientsManager({ companyId, onShowOnMap }) {
  const { t } = useLanguage();
  const [clients, setClients]             = useState([]);
  const [sections, setSections]           = useState([]);
  const [maxClients, setMaxClients]       = useState(null); // null = no limit
  const [loading, setLoading]             = useState(true);
  const [editingClient, setEditing]       = useState(null);
  const [error, setError]                 = useState("");
  const [success, setSuccess]             = useState("");
  const [search, setSearch]               = useState("");
  const [saving, setSaving]               = useState(false);
  const [modalOpen, setModalOpen]         = useState(false);

  // ── Delete state ──────────────────────────────────────────────────────────
  const [deleteModal, setDeleteModal]     = useState(false);  // single delete popup
  const [deleteTarget, setDeleteTarget]   = useState(null);   // single client to delete
  const [bulkDeleteModal, setBulkDeleteModal] = useState(false); // bulk delete popup
  const [deleting, setDeleting]           = useState(false);

  // ── Multi-select state ────────────────────────────────────────────────────
  const [selected, setSelected]           = useState(new Set()); // selected client IDs
  const [selectMode, setSelectMode]       = useState(false);     // checkbox mode on/off

  const allClientsCache = useRef(null); // cached for search
  const searchTimer     = useRef(null);

  // ── Pagination state ──────────────────────────────────────────────────────
  const PAGE_SIZE = 50;
  const [currentPage, setCurrentPage]     = useState(1);
  const [totalCount, setTotalCount]       = useState(0);
  const [pageStartDocs, setPageStartDocs] = useState([]);
  const [lastDoc, setLastDoc]             = useState(null);
  const [loadingPage, setLoadingPage]     = useState(false);
  const [searchResults, setSearchResults] = useState(null); // null = show paginated view

  const emptyForm = {
    first_name: "", last_name: "", phone: "",
    address: "", lat: "", lng: "",
    section: "", type: "", notes: "", active: true,
  };
  const [form, setForm] = useState(emptyForm);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadData(); loadPage(1, null); loadTotalCount(); }, []);

  async function loadData() {
    setLoading(true);
    try {
      const [sectionsSnap, companySnap] = await Promise.all([
        getDocs(collection(db, "companies", companyId, "sections")),
        getDoc(doc(db, "companies", companyId)),
      ]);
      setSections(sectionsSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      if (companySnap.exists()) {
        const mc = companySnap.data().max_clients;
        setMaxClients(mc != null ? mc : null);
      }
    } catch (err) {
      setError("Failed to load: " + err.message);
    }
    setLoading(false);
  }

  async function loadTotalCount() {
    try {
      const snap = await getCountFromServer(
        collection(db, "companies", companyId, "clients")
      );
      setTotalCount(snap.data().count);
    } catch (err) {
      console.error("Count error:", err.message, err.code);
    }
  }

  async function loadPage(pageNumber, startAfterDoc) {
    allClientsCache.current = null; // invalidate search cache on any page reload
    setLoadingPage(true);
    try {
      let q = query(
        collection(db, "companies", companyId, "clients"),
        orderBy("created_at", "desc"),
        limit(PAGE_SIZE)
      );
      if (startAfterDoc) {
        q = query(
          collection(db, "companies", companyId, "clients"),
          orderBy("created_at", "desc"),
          startAfter(startAfterDoc),
          limit(PAGE_SIZE)
        );
      }
      const snap = await getDocs(q);
      setClients(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLastDoc(snap.docs[snap.docs.length - 1] || null);
      setCurrentPage(pageNumber);
    } catch (err) {
      setError(`Error loading clients: ${err.message}${err.code ? ` (${err.code})` : ""}`);
    }
    setLoadingPage(false);
  }

  // Load ALL clients once and cache them for search
  async function fetchAllClientsForSearch() {
    if (allClientsCache.current) return allClientsCache.current;
    const BATCH = 1000;
    let all = [];
    let lastSnap = null;
    while (true) {
      let q = query(
        collection(db, "companies", companyId, "clients"),
        orderBy("created_at", "desc"),
        limit(BATCH)
      );
      if (lastSnap) q = query(
        collection(db, "companies", companyId, "clients"),
        orderBy("created_at", "desc"),
        startAfter(lastSnap),
        limit(BATCH)
      );
      const snap = await getDocs(q);
      all.push(...snap.docs.map(d => ({ id: d.id, ...d.data() })));
      if (snap.docs.length < BATCH) break;
      lastSnap = snap.docs[snap.docs.length - 1];
    }
    allClientsCache.current = all;
    return all;
  }

  function handleSearchChange(value) {
    setSearch(value);
    if (searchTimer.current) clearTimeout(searchTimer.current);

    if (!value.trim()) {
      setSearchResults(null);
      return;
    }

    searchTimer.current = setTimeout(async () => {
      const term = value.trim().toLowerCase();
      setLoadingPage(true);
      try {
        const all = await fetchAllClientsForSearch();
        const results = all.filter(c => {
          const name = `${c.first_name || ""} ${c.last_name || ""} ${c.name || ""}`.toLowerCase();
          const phone   = (c.phone   || "").toLowerCase();
          const address = (c.address || "").toLowerCase();
          return name.includes(term) || phone.includes(term) || address.includes(term);
        });
        setSearchResults(results);
      } catch (err) {
        console.error("Search error:", err.message);
        setSearchResults([]);
      }
      setLoadingPage(false);
    }, 300);
  }

  function detectSectionFromGPS(lat, lng, sectionsList) {
    const list = sectionsList || sections;
    if (!lat || !lng || isNaN(lat) || isNaN(lng)) return "";
    console.log(`[ZONE] Checking GPS (${lat}, ${lng}) against ${list.length} sections`);
    for (const section of list) {
      if (!section.polygon || section.polygon.length < 3) {
        console.log(`[ZONE] "${section.name || section.id}" has no polygon — skipping`);
        continue;
      }
      // Support both {lat,lng} and {latitude,longitude} coordinate formats; ensure numbers
      const polygon = section.polygon
        .map(p => ({
          latitude:  parseFloat(p.lat  ?? p.latitude),
          longitude: parseFloat(p.lng  ?? p.longitude),
        }))
        .filter(p => !isNaN(p.latitude) && !isNaN(p.longitude));
      if (polygon.length < 3) {
        console.log(`[ZONE] "${section.name || section.id}" — too few valid points after parse`);
        continue;
      }
      const hit = isPointInPolygon({ latitude: lat, longitude: lng }, polygon);
      console.log(`[ZONE] "${section.name || section.id}" (${polygon.length} pts): ${hit}`);
      if (hit) return section.id;
    }
    return "";
  }

  function resolveSectionId(nameOrId, sectionsList) {
    if (!nameOrId) return "";
    const list = sectionsList || sections;
    if (list.find(s => s.id === nameOrId)) return nameOrId;
    const byName = list.find(s => s.name?.toLowerCase().trim() === nameOrId.toLowerCase().trim());
    return byName ? byName.id : "";
  }

  function getSectionName(sectionId) {
    if (!sectionId) return "";
    return sections.find(s => s.id === sectionId)?.name || sectionId;
  }

  function flash(msg, isError = false) {
    if (isError) setError(msg); else setSuccess(msg);
    setTimeout(() => { setError(""); setSuccess(""); }, 4000);
  }

  // ── Modal open/close ──────────────────────────────────────────────────────
  function openCreate() {
    setEditing(null);
    setForm(emptyForm);
    setModalOpen(true);
  }

  function openEdit(client) {
    setEditing(client);
    setForm({
      first_name: client.first_name || client.name || "",
      last_name:  client.last_name  || "",
      phone:      client.phone      || "",
      address:    client.address    || "",
      lat:        client.location?.lat?.toString() || "",
      lng:        client.location?.lng?.toString() || "",
      section:    client.section    || "",
      type:       client.type       || "",
      notes:      client.notes      || "",
      active:     client.active !== false,
    });
    setModalOpen(true);
  }

  function closeModal() {
    setModalOpen(false);
    setEditing(null);
    setForm(emptyForm);
    setError("");
  }

  // ── Save ──────────────────────────────────────────────────────────────────
  async function handleSave(e) {
    e.preventDefault();
    setError("");
    setSaving(true);
    try {
      // Enforce max_clients limit on new client creation
      if (!editingClient && maxClients != null && totalCount >= maxClients) {
        setError(`Client limit reached (max: ${maxClients}). Contact your super admin to increase the limit.`);
        setSaving(false);
        return;
      }
      const lat = parseFloat(form.lat);
      const lng = parseFloat(form.lng);
      if (form.lat && form.lng && (isNaN(lat) || isNaN(lng)))
        throw new Error("Latitude and Longitude must be valid numbers");

      // Always use fresh sections — reload if state is empty
      let sectionsList = sections;
      if (sectionsList.length === 0) {
        const freshSnap = await getDocs(collection(db, "companies", companyId, "sections"));
        sectionsList = freshSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        if (sectionsList.length > 0) setSections(sectionsList);
      }

      const hasGPS       = form.lat && form.lng && !isNaN(lat) && !isNaN(lng);
      const autoSection  = hasGPS ? detectSectionFromGPS(lat, lng, sectionsList) : "";
      // Fallback: resolve section name from form to an ID (handles imported clients with name instead of ID)
      const resolvedForm = resolveSectionId(form.section.trim(), sectionsList);
      const finalSection = autoSection || resolvedForm;

      const data = {
        first_name: form.first_name.trim(),
        last_name:  form.last_name.trim(),
        name:       `${form.first_name.trim()} ${form.last_name.trim()}`.trim(),
        phone:      form.phone.trim(),
        address:    form.address.trim(),
        section:    finalSection,
        type:       form.type.trim(),
        notes:      form.notes.trim(),
        location:   hasGPS ? { lat, lng } : null,
        active:     form.active,
        updated_at: serverTimestamp(),
      };

      if (editingClient) {
        await updateDoc(doc(db, "companies", companyId, "clients", editingClient.id), data);
        setClients(prev => prev.map(c => c.id === editingClient.id ? { ...c, ...data } : c));
        flash(autoSection ? `✅ Updated — zone "${getSectionName(autoSection)}"` : "✅ Client updated!");
      } else {
        const clientId = "client-" + Date.now().toString(36);
        data.created_at = serverTimestamp();
        await setDoc(doc(db, "companies", companyId, "clients", clientId), data);
        try {
          await updateDoc(doc(db, "companies", companyId), { client_count: increment(1) });
        } catch (e) { console.error("[COUNTER] client_count increment failed:", e.message); }
        setTotalCount(prev => prev + 1);
        setClients(prev => [{ id: clientId, ...data }, ...prev]);
        flash(autoSection ? `✅ Created — zone "${getSectionName(autoSection)}"` : "✅ Client created!");
      }
      closeModal();
    } catch (err) {
      setError("Error: " + err.message);
    }
    setSaving(false);
  }

  // ── Single delete ─────────────────────────────────────────────────────────
  function askDelete(client) {
    setDeleteTarget(client);
    setDeleteModal(true);
  }

  async function confirmSingleDelete() {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteDoc(doc(db, "companies", companyId, "clients", deleteTarget.id));
      try {
        await updateDoc(doc(db, "companies", companyId), { client_count: increment(-1) });
      } catch (e) { console.error("[COUNTER] client_count decrement failed:", e.message); }
      setTotalCount(prev => Math.max(0, prev - 1));
      setClients(prev => prev.filter(c => c.id !== deleteTarget.id));
      setSelected(prev => { const n = new Set(prev); n.delete(deleteTarget.id); return n; });
      setDeleteModal(false);
      setDeleteTarget(null);
      flash("✅ Client deleted.");
    } catch (err) {
      flash("Error: " + err.message, true);
    }
    setDeleting(false);
  }

  // ── Bulk delete ───────────────────────────────────────────────────────────
  async function confirmBulkDelete() {
    if (selected.size === 0) return;
    setDeleting(true);
    const deletedCount = selected.size;
    try {
      // Firestore batch supports up to 500 operations
      const ids    = Array.from(selected);
      const chunks = [];
      for (let i = 0; i < ids.length; i += 500) chunks.push(ids.slice(i, i + 500));

      for (const chunk of chunks) {
        const batch = writeBatch(db);
        chunk.forEach(id => batch.delete(doc(db, "companies", companyId, "clients", id)));
        await batch.commit();
      }

      try {
        await updateDoc(doc(db, "companies", companyId), { client_count: increment(-deletedCount) });
      } catch (e) { console.error("[COUNTER] client_count bulk decrement failed:", e.message); }
      setTotalCount(prev => Math.max(0, prev - deletedCount));
      setClients(prev => prev.filter(c => !selected.has(c.id)));
      flash(`✅ Deleted ${deletedCount} client(s).`);
      setSelected(new Set());
      setSelectMode(false);
      setBulkDeleteModal(false);
    } catch (err) {
      flash("Error: " + err.message, true);
    }
    setDeleting(false);
  }

  // ── Selection helpers ─────────────────────────────────────────────────────
  function toggleSelect(id) {
    setSelected(prev => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  }

  function toggleSelectAll() {
    if (selected.size === filtered.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(filtered.map(c => c.id)));
    }
  }

  function exitSelectMode() {
    setSelectMode(false);
    setSelected(new Set());
  }

  // ── Toggle active ─────────────────────────────────────────────────────────
  async function toggleActive(client, value) {
    try {
      await updateDoc(doc(db, "companies", companyId, "clients", client.id), {
        active: value, updated_at: serverTimestamp(),
      });
      setClients(prev => prev.map(c => c.id === client.id ? { ...c, active: value } : c));
    } catch (err) { flash("Error: " + err.message, true); }
  }

  // When searching: show Firestore search results; otherwise: show current page
  const filtered = searchResults !== null ? searchResults : clients;

  const allFilteredSelected = filtered.length > 0 && filtered.every(c => selected.has(c.id));

  if (loading) return <div className="bg-white rounded-xl shadow-sm p-6"><p className="text-gray-500 text-sm">{t("clients_loading")}</p></div>;

  return (
    <div className="space-y-4">

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="bg-white rounded-xl shadow-sm p-4 md:p-6 flex flex-col sm:flex-row justify-between sm:items-center gap-3">
        <div>
          <h2 className="text-lg font-semibold text-gray-800">{t("clients_title")}</h2>
          <p className="text-gray-500 text-sm mt-1">{totalCount} {t("clients_title")?.toLowerCase()}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Select mode toggle */}
          {!selectMode ? (
            <button onClick={() => setSelectMode(true)}
              className="bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium px-3 py-2 rounded-lg">
              ☑️ {t("clients_select")}
            </button>
          ) : (
            <button onClick={exitSelectMode}
              className="bg-gray-200 text-gray-700 text-sm font-medium px-3 py-2 rounded-lg">
              {t("clients_cancel")}
            </button>
          )}

          <button onClick={openCreate}
            className="bg-green-600 hover:bg-green-700 text-white text-sm font-medium px-4 py-2 rounded-lg">
            {t("clients_add")}
          </button>
        </div>
      </div>

      {/* ── Bulk action bar (shown when items selected) ─────────────────────── */}
      {selectMode && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl px-5 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {/* Select all checkbox */}
            <input type="checkbox" checked={allFilteredSelected} onChange={toggleSelectAll}
              className="w-4 h-4 accent-green-600 cursor-pointer" />
            <span className="text-sm text-blue-800 font-medium">
              {selected.size === 0
                ? t("clients_select_for_bulk")
                : `${selected.size} ${t("clients_selected_count")}`}
            </span>
          </div>
          {selected.size > 0 && (
            <button onClick={() => setBulkDeleteModal(true)}
              className="bg-red-600 hover:bg-red-700 text-white text-sm font-medium px-4 py-2 rounded-lg flex items-center gap-2">
              🗑 {t("clients_delete_selected")} ({selected.size})
            </button>
          )}
        </div>
      )}

      {/* ── Feedback ───────────────────────────────────────────────────────── */}
      {error   && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm">{error}</div>}
      {success && <div className="bg-green-50 border border-green-200 text-green-700 rounded-lg p-3 text-sm">{success}</div>}

      {/* ── Search ─────────────────────────────────────────────────────────── */}
      {totalCount > 0 && (
        <div className="bg-white rounded-xl shadow-sm p-4">
          <input value={search} onChange={e => handleSearchChange(e.target.value)}
            placeholder={t("clients_search_ph")}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
        </div>
      )}

      {/* ── Clients List ───────────────────────────────────────────────────── */}
      {(
        loadingPage ? (
          <div className="bg-white rounded-xl shadow-sm p-8 text-center">
            <p className="text-gray-400 text-sm">{t("clients_loading")}</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm p-12 text-center">
            <ClipboardList className="w-10 h-10 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-600 font-medium">{search ? t("clients_no_results") : t("clients_no_clients")}</p>
            <p className="text-gray-400 text-sm mt-1">{search ? t("clients_try_search") : t("clients_add_manually")}</p>
            {!search && (
              <div className="flex gap-3 justify-center mt-4">
                <button onClick={openCreate} className="bg-green-600 text-white text-sm font-medium px-4 py-2 rounded-lg">{t("clients_add")}</button>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map(client => {
              const isSelected = selected.has(client.id);
              return (
                <div key={client.id}
                  className={`bg-white rounded-xl shadow-sm p-5 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 transition ${
                    isSelected ? "ring-2 ring-blue-400 bg-blue-50" : ""
                  }`}>
                  <div className="flex items-center gap-4">
                    {/* Checkbox in select mode */}
                    {selectMode && (
                      <input type="checkbox" checked={isSelected} onChange={() => toggleSelect(client.id)}
                        className="w-4 h-4 accent-blue-600 cursor-pointer flex-shrink-0" />
                    )}

                    {/* Avatar */}
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center font-bold ${
                      client.active !== false ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-400"
                    }`}>
                      {(client.first_name || client.name || "?").charAt(0).toUpperCase()}
                    </div>

                    <div>
                      <p className="font-medium text-gray-800">
                        {client.first_name} {client.last_name}{!client.first_name && client.name}
                      </p>
                      {client.phone   && <p className="text-gray-400 text-xs mt-0.5">📞 {client.phone}</p>}
                      {client.address && <p className="text-gray-400 text-xs">📍 {client.address}</p>}
                      <div className="flex flex-wrap gap-1 mt-0.5">
                        {client.section && (
                          <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium">
                            🗂 {getSectionName(client.section)}
                          </span>
                        )}
                        {client.type && (
                          <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">🏷 {client.type}</span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500">{client.active !== false ? t("active") : t("inactive")}</span>
                      <UISwitch value={client.active !== false} onChange={val => toggleActive(client, val)} size="sm" />
                    </div>
                    {(client.location || (client.lat && client.lng)) && onShowOnMap && (
                      <button
                        onClick={() => onShowOnMap(client)}
                        title="Show on Live Map"
                        className="text-xs px-3 py-1.5 bg-green-50 text-green-600 hover:bg-green-100 rounded-lg font-medium flex items-center gap-1">
                        <MapPin className="w-3 h-3" /> Map
                      </button>
                    )}
                    <button onClick={() => openEdit(client)}
                      className="text-xs px-3 py-1.5 bg-blue-50 text-blue-600 hover:bg-blue-100 rounded-lg font-medium">
                      Edit
                    </button>
                    <button onClick={() => askDelete(client)}
                      className="text-xs px-3 py-1.5 bg-red-50 text-red-600 hover:bg-red-100 rounded-lg font-medium">
                      Delete
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )
      )}

      {/* ── Pagination bar ─────────────────────────────────────────────────── */}
      {searchResults === null && totalCount > PAGE_SIZE && (
        <div className="bg-white rounded-xl shadow-sm p-4 flex items-center justify-between">
          <p className="text-sm text-gray-500">
            {t("clients_showing")} {Math.min((currentPage - 1) * PAGE_SIZE + 1, totalCount)}–{Math.min(currentPage * PAGE_SIZE, totalCount)} {t("clients_of")} {totalCount}
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                const prevStart = pageStartDocs[currentPage - 3] || null;
                setPageStartDocs(prev => prev.slice(0, -1));
                loadPage(currentPage - 1, prevStart);
              }}
              disabled={currentPage === 1 || loadingPage}
              className="px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg disabled:opacity-40 flex items-center gap-1">
              <ChevronLeft className="w-4 h-4" /> {t("clients_previous")}
            </button>
            <span className="text-sm font-medium text-gray-700 px-2">
              {t("clients_page")} {currentPage} {t("clients_of")} {Math.ceil(totalCount / PAGE_SIZE)}
            </span>
            <button
              onClick={() => {
                setPageStartDocs(prev => [...prev, lastDoc]);
                loadPage(currentPage + 1, lastDoc);
              }}
              disabled={currentPage * PAGE_SIZE >= totalCount || loadingPage}
              className="px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg disabled:opacity-40 flex items-center gap-1">
              {t("clients_next")} <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════════ */}
      {/* ── Single Delete Confirmation Modal ──────────────────────────────── */}
      {/* ═══════════════════════════════════════════════════════════════════ */}
      <Modal isOpen={deleteModal} onClose={() => { setDeleteModal(false); setDeleteTarget(null); }} title={t("clients_delete_title")} size="sm">
        <div className="space-y-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
              <span className="text-red-600 text-lg">🗑</span>
            </div>
            <div>
              <p className="text-gray-800 font-medium">
                Delete "{deleteTarget?.first_name} {deleteTarget?.last_name || deleteTarget?.name}"?
              </p>
              <p className="text-gray-500 text-sm mt-1">
                {t("clients_cannot_undo_full")}
              </p>
            </div>
          </div>
          <div className="flex gap-3 pt-2 border-t">
            <button onClick={confirmSingleDelete} disabled={deleting}
              className="bg-red-600 hover:bg-red-700 text-white text-sm font-medium px-5 py-2 rounded-lg disabled:opacity-50 flex items-center gap-2">
              {deleting ? t("deleting") : `🗑 ${t("yes_delete")}`}
            </button>
            <button onClick={() => { setDeleteModal(false); setDeleteTarget(null); }}
              className="bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium px-5 py-2 rounded-lg">
              {t("cancel")}
            </button>
          </div>
        </div>
      </Modal>

      {/* ═══════════════════════════════════════════════════════════════════ */}
      {/* ── Bulk Delete Confirmation Modal ────────────────────────────────── */}
      {/* ═══════════════════════════════════════════════════════════════════ */}
      <Modal isOpen={bulkDeleteModal} onClose={() => setBulkDeleteModal(false)} title={t("clients_bulk_delete")} size="sm">
        <div className="space-y-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
              <span className="text-red-600 text-lg">⚠️</span>
            </div>
            <div>
              <p className="text-gray-800 font-medium">
                Delete {selected.size} client{selected.size > 1 ? "s" : ""}?
              </p>
              <p className="text-gray-500 text-sm mt-1">
                {t("clients_delete_msg")}
              </p>
            </div>
          </div>

          {/* Preview of selected clients */}
          <div className="bg-gray-50 rounded-lg p-3 max-h-40 overflow-y-auto space-y-1">
            {Array.from(selected).map(id => {
              const c = clients.find(x => x.id === id);
              if (!c) return null;
              return (
                <p key={id} className="text-xs text-gray-600">
                  • {c.first_name} {c.last_name || c.name}
                  {c.phone ? ` — ${c.phone}` : ""}
                </p>
              );
            })}
          </div>

          <div className="flex gap-3 pt-2 border-t">
            <button onClick={confirmBulkDelete} disabled={deleting}
              className="bg-red-600 hover:bg-red-700 text-white text-sm font-medium px-5 py-2 rounded-lg disabled:opacity-50 flex items-center gap-2">
              {deleting ? t("deleting") : `🗑 ${t("delete_all")} (${selected.size})`}
            </button>
            <button onClick={() => setBulkDeleteModal(false)}
              className="bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium px-5 py-2 rounded-lg">
              {t("cancel")}
            </button>
          </div>
        </div>
      </Modal>

      {/* ═══════════════════════════════════════════════════════════════════ */}
      {/* ── Edit / Create Modal ───────────────────────────────────────────── */}
      {/* ═══════════════════════════════════════════════════════════════════ */}
      <Modal isOpen={modalOpen} onClose={closeModal}
        title={editingClient ? `${t("edit")} — ${editingClient.first_name || editingClient.name} ${editingClient.last_name || ""}` : t("clients_new")}
        size="lg">
        <form onSubmit={handleSave} className="space-y-4">
          {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm">{error}</div>}
          <p className="text-xs text-blue-600">{t("clients_zone_hint")}</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t("form_first_name")} *</label>
              <input value={form.first_name} onChange={e => setForm(p => ({...p, first_name: e.target.value}))} required
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500" placeholder={t("form_first_name")} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t("form_last_name")}</label>
              <input value={form.last_name} onChange={e => setForm(p => ({...p, last_name: e.target.value}))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500" placeholder={t("form_last_name")} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t("form_phone")} *</label>
              <input value={form.phone} onChange={e => setForm(p => ({...p, phone: e.target.value}))} required
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500" placeholder="+971 50 000 0000" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t("clients_type_category")}</label>
              <input value={form.type} onChange={e => setForm(p => ({...p, type: e.target.value}))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500" placeholder="e.g. Pharmacy" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t("clients_zone_auto")} <span className="text-blue-500 text-xs">{t("clients_auto_from_gps")}</span></label>
              <select value={form.section} onChange={e => setForm(p => ({...p, section: e.target.value}))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500">
                <option value="">{t("clients_auto_detect")}</option>
                {sections.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t("form_address")}</label>
              <input value={form.address} onChange={e => setForm(p => ({...p, address: e.target.value}))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500" placeholder={t("form_address")} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t("clients_latitude")}</label>
              <input value={form.lat} onChange={e => setForm(p => ({...p, lat: e.target.value}))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500" placeholder="25.2048" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t("clients_longitude")}</label>
              <input value={form.lng} onChange={e => setForm(p => ({...p, lng: e.target.value}))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500" placeholder="55.2708" />
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">{t("form_notes")}</label>
              <textarea value={form.notes} onChange={e => setForm(p => ({...p, notes: e.target.value}))} rows={2}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500" placeholder={t("clients_notes_ph")} />
            </div>
            <div className="col-span-2 flex items-center gap-3">
              <label className="text-sm font-medium text-gray-700">{t("clients_status")}</label>
              <UISwitch value={form.active} onChange={val => setForm(p => ({ ...p, active: val }))} />
              <span className="text-sm text-gray-500">{form.active ? t("active") : t("inactive")}</span>
            </div>

            {/* Created by / Created at — read-only, shown when editing only */}
            {editingClient && (editingClient.created_by_name || editingClient.created_by || editingClient.created_at) && (
              <div className="col-span-2 bg-gray-50 rounded-lg p-3 space-y-2">
                {(editingClient.created_by_name || editingClient.created_by) && (
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">{t("clients_created_by")}</span>
                    <span className="text-xs font-medium text-gray-800">
                      {editingClient.created_by_name || editingClient.created_by}
                    </span>
                  </div>
                )}
                {editingClient.created_at && (
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">{t("th_date")}</span>
                    <span className="text-xs text-gray-600">
                      {editingClient.created_at?.toDate
                        ? editingClient.created_at.toDate().toLocaleDateString()
                        : "—"}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="flex gap-3 pt-2 border-t">
            <button type="submit" disabled={saving}
              className="bg-green-600 hover:bg-green-700 text-white text-sm font-medium px-5 py-2 rounded-lg disabled:opacity-50">
              {saving ? t("saving") : editingClient ? t("save_changes") : t("clients_add")}
            </button>
            <button type="button" onClick={closeModal}
              className="bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium px-5 py-2 rounded-lg">{t("cancel")}</button>
          </div>
        </form>
      </Modal>

    </div>
  );
}

export default ClientsManager;
